import { DEBUG, DEBUG_ALERT_MSG } from '../config';
import { reportClientError } from './clientErrorReporter';
import { throttle } from './schedulers';

window.addEventListener('error', handleErrorEvent);
window.addEventListener('unhandledrejection', handleErrorEvent);

const RESOURCE_ERROR_MESSAGES = new Set([
  'The source image could not be decoded.',
]);
const IGNORED_RUNTIME_ERROR_MESSAGES = new Set([
  'WEB_AUTH_TOKEN_FAILED',
  'AUTH_KEY_UNREGISTERED',
  'Not connected',
  'Cannot send requests while disconnected',
  'HttpStream was closed',
  'WebSocket was closed',
]);

function getErrorMessageText(value: unknown) {
  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Error) {
    return value.message || String(value);
  }

  if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
    return value.message;
  }

  return String(value);
}

function reportResourceObservation(message: string, source: 'window.error' | 'unhandledrejection') {
  reportClientError({
    kind: 'resource',
    message,
    metadata: {
      source,
      resourceType: 'media-decode',
    },
  });
}

// Non-blocking error overlay for debug mode (replaces window.alert which blocks CDP)
let overlayEl: HTMLDivElement | undefined;
let overlayTimeout: ReturnType<typeof setTimeout> | undefined;

function showDebugOverlay(msg: string) {
  if (!overlayEl) {
    overlayEl = document.createElement('div');
    Object.assign(overlayEl.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      zIndex: '99999',
      background: '#dc2626',
      color: '#fff',
      padding: '8px 12px',
      fontSize: '12px',
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      maxHeight: '40vh',
      overflow: 'auto',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    });
    overlayEl.addEventListener('click', () => {
      overlayEl!.style.display = 'none';
    });
    document.body.appendChild(overlayEl);
  }
  overlayEl.textContent = msg;
  overlayEl.style.display = 'block';

  // Also expose for CDP inspection
  (window as any).__lastDebugError = msg;

  clearTimeout(overlayTimeout);
  overlayTimeout = setTimeout(() => {
    if (overlayEl) overlayEl.style.display = 'none';
  }, 15000);
}

const throttleError = throttle((err: Error) => {
  showDebugOverlay(getDebugOverlayMessage(err));
}, 1500);

export function handleError(err: Error) {
  if (IGNORED_RUNTIME_ERROR_MESSAGES.has(err?.message || '')) {
    return;
  }

  // eslint-disable-next-line no-console
  console.error(err);

  reportClientError({
    kind: 'runtime',
    message: err?.message || String(err),
    stack: err?.stack || '',
    metadata: {
      source: 'runtime',
    },
  });
  if (DEBUG) {
    throttleError(err);
  }
}

function handleErrorEvent(e: ErrorEvent | PromiseRejectionEvent) {
  // Ignore resource loading errors (failed img/video/audio/link loads).
  // These reach the window via capture in Chrome but are not actionable JS errors.
  if (e instanceof ErrorEvent && !e.message && !e.error && (e as any).target instanceof HTMLElement) {
    return;
  }
  if (!(e instanceof ErrorEvent) && !(e instanceof PromiseRejectionEvent)) {
    return;
  }

  const rawError = e instanceof ErrorEvent ? (e.error || e.message) : e.reason;
  const errorMsg = getErrorMessageText(rawError);

  // Auto-reload on chunk load failure (stale deploy). One reload per 10s to prevent loops.
  if (errorMsg.includes('Loading chunk') || rawError?.name === 'ChunkLoadError') {
    const RELOAD_KEY = 'chunk_reload_ts';
    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - lastReload > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return;
    }
  }

  if (
    RESOURCE_ERROR_MESSAGES.has(errorMsg)
  ) {
    e.preventDefault();
    reportResourceObservation(errorMsg, e instanceof ErrorEvent ? 'window.error' : 'unhandledrejection');
    return;
  }

  // Ignore image/media load errors that surface as unhandled promise rejections
  // (e.g. from preloadImage when a blob URL becomes invalid)
  if (e instanceof PromiseRejectionEvent && e.reason instanceof Event
    && e.reason.type === 'error' && (e.reason as any).target instanceof HTMLElement) {
    e.preventDefault();
    return;
  }

  if (e instanceof ErrorEvent) {
    // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
    if (e.message === 'ResizeObserver loop limit exceeded') {
      return;
    }

    // Flood wait errors
    if (e.message.includes('A wait of')) {
      return;
    }
  }

  e.preventDefault();
  handleError(rawError instanceof Error ? rawError : new Error(errorMsg));
}

function getDebugOverlayMessage(err: Error) {
  return `${DEBUG_ALERT_MSG}\n\n${(err?.message) || err}\n${err?.stack}`;
}
